import { ReactElement, useState } from "react";
import {
  Box,
  Autocomplete,
  TextField,
  Typography,
  IconButton,
  Paper,
  Stack,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIconButton from "./DeleteIconButton";

interface ListItemAutocompleteProps {
  /**
   * Array of list items
   */
  items: string[];
  /**
   * Callback when items change
   */
  onChange: (items: string[]) => void;
  /**
   * Label for the input field
   */
  label?: string;
  /**
   * Placeholder text when no items exist
   */
  placeholder?: string;
  /**
   * Detail name being edited (for checking if items are used)
   */
  detailName?: string;
  /**
   * Category name (for checking if items are used in transactions)
   */
  categoryName?: string;
  /**
   * Group name (for checking if items are used in transactions for any category in the group)
   */
  groupName?: string;
  /**
   * Categories array (for group-level checking - to find all categories in the group)
   */
  categories?: Array<{ name: string; groupId?: string }>;
  /**
   * Transactions array (for checking if items are used in transactions)
   */
  transactions?: Array<{ category?: string; details?: Record<string, string | number> }>;
  /**
   * Group name (for checking if items are used in accounts - legacy support)
   */
  editingGroupName?: string;
  /**
   * Accounts array (for checking if items are used in accounts - legacy support)
   */
  accounts?: Array<{ group: string; details?: Record<string, string | number> }>;
}

// Sortable List Item Card Component
interface SortableListItemCardProps {
  item: string;
  index: number;
  isItemUsed: boolean;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
  disabledTooltip?: string;
}

function SortableListItemCard({
  item,
  index,
  isItemUsed,
  onEdit,
  onRemove,
  disabledTooltip,
}: SortableListItemCardProps): ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(index) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 4 : 0}
      sx={{
        p: 1,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        "&:hover": { boxShadow: 2 },
        transition: "box-shadow 0.2s",
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <IconButton
          {...attributes}
          {...listeners}
          size="small"
          sx={{
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
            color: "text.secondary",
            p: 0.5,
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight="medium">
            {item}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={() => onEdit(index)}
            sx={{ color: "primary.main" }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <DeleteIconButton
            onClick={() => onRemove(index)}
            disabled={isItemUsed}
            disabledTooltip={disabledTooltip}
            size="small"
          />
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * List Item Autocomplete Component
 * 
 * Allows users to add and manage list items with autocomplete functionality.
 * Shows existing items in dropdown when input is empty, and "Create" button
 * when typing a new item that doesn't exist.
 */
function ListItemAutocomplete({
  items,
  onChange,
  label = "List Items",
  placeholder = "Type to add items (e.g., Bank Name)",
  detailName,
  categoryName,
  groupName,
  categories = [],
  transactions = [],
  editingGroupName,
  accounts = [],
}: ListItemAutocompleteProps): ReactElement {
  const [inputValue, setInputValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Check if a list item is used in any transaction or account
  const isListItemInUse = (listItem: string): boolean => {
    if (!detailName) return false;
    
    // Check transactions (for group-level details - check all categories in the group)
    if (groupName && categories.length > 0 && transactions.length > 0) {
      // Get category names that belong to this group
      // The caller should pass categories filtered to only include those in the group
      // We'll use all passed categories as they should already be filtered
      const groupCategoryNames = categories.map((cat) => cat.name);
      
      // Check if any transaction uses this list item for any category in the group
      const isUsedInTransaction = transactions.some((transaction) => {
        if (!transaction.category) return false;
        
        // Check if transaction belongs to a category in this group
        if (!groupCategoryNames.includes(transaction.category)) {
          return false;
        }
        
        // Check if transaction has details
        if (!transaction.details || typeof transaction.details !== 'object') {
          return false;
        }
        
        // Check if the transaction has this detail and the value matches the list item
        const detailValue = transaction.details[detailName];
        return detailValue !== undefined && String(detailValue) === listItem;
      });
      
      if (isUsedInTransaction) return true;
    }
    
    // Check transactions (for category-level details - single category)
    if (categoryName && !groupName && transactions.length > 0) {
      const isUsedInTransaction = transactions.some((transaction) => {
        // Check if transaction belongs to this category
        if (!transaction.category || transaction.category !== categoryName) {
          return false;
        }
        
        // Check if transaction has details
        if (!transaction.details || typeof transaction.details !== 'object') {
          return false;
        }
        
        // Check if the transaction has this detail and the value matches the list item
        const detailValue = transaction.details[detailName];
        return detailValue !== undefined && String(detailValue) === listItem;
      });
      
      if (isUsedInTransaction) return true;
    }
    
    // Check accounts (for account group details - legacy support)
    if (editingGroupName && accounts.length > 0) {
      const isUsedInAccount = accounts.some((account) => {
        if (account.group !== editingGroupName) return false;
        if (!account.details) return false;
        // Check if the account has this detail and the value matches the list item
        const detailValue = account.details[detailName];
        return detailValue !== undefined && String(detailValue) === listItem;
      });
      
      if (isUsedInAccount) return true;
    }
    
    return false;
  };

  const handleRemoveItem = (index: number): void => {
    const item = items[index];
    // Don't remove if item is in use
    if (isListItemInUse(item)) {
      return;
    }
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
    
    // Update editing index if affected
    if (editingItemIndex !== null) {
      if (editingItemIndex === index) {
        setEditingItemIndex(null);
        setInputValue("");
      } else if (editingItemIndex > index) {
        setEditingItemIndex(editingItemIndex - 1);
      }
    }
  };

  const handleCreateItem = (): void => {
    if (!inputValue.trim()) return;
    
    const trimmedValue = inputValue.trim();
    
    // If editing, update the item
    if (editingItemIndex !== null) {
      // Check for duplicate (excluding current item)
      const duplicate = items.some(
        (item, i) => i !== editingItemIndex && item.toLowerCase() === trimmedValue.toLowerCase()
      );
      if (duplicate) {
        return;
      }
      
      const newItems = [...items];
      newItems[editingItemIndex] = trimmedValue;
      onChange(newItems);
      setEditingItemIndex(null);
      setInputValue("");
      setOpen(false);
      setIsFocused(false);
    } else {
      // Adding new item - check for duplicate
      if (items.some(item => item.toLowerCase() === trimmedValue.toLowerCase())) {
        return;
      }
      onChange([...items, trimmedValue]);
      setInputValue("");
      setOpen(false);
      setIsFocused(false);
    }
  };

  const handleEditItem = (index: number): void => {
    setEditingItemIndex(index);
    setInputValue(items[index]);
    setIsFocused(true);
    setOpen(true); // Open dropdown to show Update option
  };

  const handleItemDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = parseInt(String(active.id));
    const newIndex = parseInt(String(over.id));

    if (isNaN(oldIndex) || isNaN(newIndex)) {
      return;
    }

    // Reorder items
    const reorderedItems = arrayMove(items, oldIndex, newIndex);
    onChange(reorderedItems);
    
    // Update editingItemIndex if it was affected
    if (editingItemIndex !== null) {
      if (editingItemIndex === oldIndex) {
        setEditingItemIndex(newIndex);
      } else if (editingItemIndex > oldIndex && editingItemIndex <= newIndex) {
        setEditingItemIndex(editingItemIndex - 1);
      } else if (editingItemIndex < oldIndex && editingItemIndex >= newIndex) {
        setEditingItemIndex(editingItemIndex + 1);
      }
    }
  };
  
  const getDisabledTooltip = (item: string): string | undefined => {
    if (!isListItemInUse(item)) return undefined;
    
    if (groupName) {
      return `Cannot delete "${item}": It is used in one or more transactions for categories in this group`;
    } else if (categoryName) {
      return `Cannot delete "${item}": It is used in one or more transactions`;
    } else if (editingGroupName) {
      return `Cannot delete "${item}": It is used in one or more accounts`;
    }
    return undefined;
  };

  return (
    <Box>
      <Autocomplete
        freeSolo
        open={open}
        onOpen={() => {
          setOpen(true);
          setIsFocused(true);
        }}
        onClose={(_, reason) => {
          // Don't close if user is editing and pressing Escape - let them cancel first
          if (editingItemIndex !== null && reason === "escape") {
            // Reset editing state when Escape is pressed
            setEditingItemIndex(null);
            setInputValue("");
            setOpen(false);
            setIsFocused(false);
            return;
          }
          setOpen(false);
          setIsFocused(false);
          // Reset editing state if input is cleared
          if (!inputValue.trim() && editingItemIndex !== null) {
            setEditingItemIndex(null);
            setInputValue("");
          }
        }}
        options={items}
        value={null}
        inputValue={inputValue}
        slotProps={{
          popper: { disablePortal: true },
        }}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
          if (newInputValue.trim()) {
            setIsFocused(true);
            // Keep dropdown open when typing
            if (!open) {
              setOpen(true);
            }
          }
        }}
        onChange={(_, newValue) => {
          // Handle Enter key or selection - create or update item
          if (typeof newValue === "string" && newValue !== "__CREATE__") {
            // User selected an existing item - don't do anything, let them type instead
            return;
          }
        }}
        filterOptions={(options, params) => {
          const filterInputValue = params?.inputValue ?? "";
          
          // When editing, always show Update option if there's input
          if (editingItemIndex !== null && filterInputValue.trim()) {
            const filtered = options.filter((option) =>
              option.toLowerCase().includes(filterInputValue.toLowerCase())
            );
            // Always show Update option when editing
            return [...filtered, "__CREATE__"];
          }
          
          // When input is empty, show all items (no filtering, no Create option)
          if (!filterInputValue.trim()) {
            return options;
          }
          
          // When input has text, filter options and add Create if it doesn't match
          const filtered = options.filter((option) =>
            option.toLowerCase().includes(filterInputValue.toLowerCase())
          );
          
          // If input doesn't match any existing option, add create marker
          const trimmedInput = filterInputValue.trim().toLowerCase();
          const matchesExisting = filtered.some(option => option.toLowerCase() === trimmedInput);
          
          if (!matchesExisting) {
            return [...filtered, "__CREATE__"];
          }
          
          return filtered;
        }}
        getOptionLabel={(option) => {
          if (option === "__CREATE__") {
            return "";
          }
          return option;
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              label={label}
              placeholder={editingItemIndex !== null ? `Editing "${items[editingItemIndex]}"...` : placeholder}
              onKeyDown={(e) => {
                if (e.key === "Enter" && inputValue.trim()) {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCreateItem();
                } else if (e.key === "Escape" && editingItemIndex !== null) {
                  // Cancel editing on Escape
                  setEditingItemIndex(null);
                  setInputValue("");
                  setOpen(false);
                  setIsFocused(false);
                }
              }}
            />
          );
        }}
        renderOption={(props, option) => {
          if (option === "__CREATE__") {
            const isEditing = editingItemIndex !== null;
            const trimmed = inputValue.trim();
            return (
              <li {...props} key="__CREATE__">
                <Box 
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: 1, 
                    width: "100%",
                    py: 0.5,
                    px: 1,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "action.hover"
                    }
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCreateItem();
                  }}
                >
                  {isEditing ? (
                    <EditIcon sx={{ fontSize: "small", color: "primary.main" }} />
                  ) : (
                    <AddIcon sx={{ fontSize: "small", color: "primary.main" }} />
                  )}
                  <Typography sx={{ color: "primary.main" }}>
                    {isEditing ? `Save as "${trimmed}"` : `Add "${trimmed}"`}
                  </Typography>
                </Box>
              </li>
            );
          }
          return null; // Don't show items in dropdown anymore - they're shown below
        }}
      />
      
      {/* Show added items below the input with drag and drop, edit, and delete */}
      {items.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleItemDragEnd}
          >
            <SortableContext
              items={items.map((_, index) => String(index))}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={1}>
                {items.map((item, index) => {
                  const itemInUse = isListItemInUse(item);
                  return (
                    <SortableListItemCard
                      key={index}
                      item={item}
                      index={index}
                      isItemUsed={itemInUse}
                      onEdit={handleEditItem}
                      onRemove={handleRemoveItem}
                      disabledTooltip={getDisabledTooltip(item)}
                    />
                  );
                })}
              </Stack>
            </SortableContext>
          </DndContext>
        </Box>
      )}
    </Box>
  );
}

export default ListItemAutocomplete;
